import { type ReactNode } from 'react'
import { Link, Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom'
import { Bell, BookOpen, ClipboardList, CreditCard, FileText, Home, Menu, Network, Plus, Settings, Shield, Users } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { ThemeDropdown } from '@/components/ThemeDropdown'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

interface LayoutProps {
  children?: ReactNode
}

interface NavItem {
  href: string
  label: string
  icon: typeof Home
  matchers: string[]
}

const primaryNavItems: NavItem[] = [
  {
    href: '/overview',
    label: 'Overview',
    icon: Home,
    matchers: ['/overview', '/dashboard', '/jobs/:id'],
  },
  {
    href: '/validations',
    label: 'Validations',
    icon: ClipboardList,
    matchers: ['/validations', '/validations/:id'],
  },
  {
    href: '/networks',
    label: 'Networks',
    icon: Network,
    matchers: ['/networks', '/networks/add', '/networks/:id', '/networks/:id/settings'],
  },
  {
    href: '/settings',
    label: 'Settings',
    icon: Settings,
    matchers: ['/settings'],
  },
]

const isNavItemActive = (pathname: string, item: NavItem) =>
  item.matchers.some((matcher) =>
    Boolean(matchPath({ path: matcher, end: matcher === item.href }, pathname)),
  )

function AppShellNavLink({
  item,
  pathname,
  compact = false,
  mobile = false,
}: {
  item: NavItem
  pathname: string
  compact?: boolean
  mobile?: boolean
}) {
  const Icon = item.icon
  const active = isNavItemActive(pathname, item)

  return (
    <Link
      to={item.href}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group flex min-w-0 items-center gap-3 rounded-md text-[13px] font-medium tracking-tight transition-colors',
        compact ? 'px-3 py-2' : 'px-3 py-2.5',
        mobile && 'rounded-lg px-3 py-2',
        active
          ? 'bg-background text-primary shadow-sm ring-1 ring-border'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center transition-colors',
          compact ? 'h-4 w-4' : 'h-[18px] w-[18px]',
          active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground',
        )}
      >
        <Icon className={compact ? 'h-4 w-4' : 'h-[18px] w-[18px]'} />
      </span>
      <span className="min-w-0 truncate">{item.label}</span>
    </Link>
  )
}

const adminNavItems: NavItem[] = [
  {
    href: '/admin',
    label: 'Dashboard',
    icon: Shield,
    matchers: ['/admin'],
  },
  {
    href: '/admin/users',
    label: 'Users',
    icon: Users,
    matchers: ['/admin/users', '/admin/users/:id'],
  },
  {
    href: '/admin/billing',
    label: 'Billing',
    icon: CreditCard,
    matchers: ['/admin/billing'],
  },
  {
    href: '/admin/beta-signups',
    label: 'Beta Signups',
    icon: ClipboardList,
    matchers: ['/admin/beta-signups'],
  },
  {
    href: '/admin/audit-logs',
    label: 'Audit Logs',
    icon: FileText,
    matchers: ['/admin/audit-logs'],
  },
]

export function Layout({ children }: LayoutProps) {
  const { user, isAdmin, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const content = children ?? <Outlet />
  const accountInitial = user?.email?.trim().charAt(0).toUpperCase() || 'A'

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 w-full max-w-[112rem] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                className="h-8 w-8 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
              >
                  <Menu className="h-4 w-4" />
                  <span className="sr-only">Open navigation menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                <DropdownMenuLabel>Navigate</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {primaryNavItems.map((item) => (
                  <DropdownMenuItem key={item.href} onClick={() => navigate(item.href)}>
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/networks/add')}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Network
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/docs')}>
                  <BookOpen className="mr-2 h-4 w-4" />
                  Docs
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Admin</DropdownMenuLabel>
                    {adminNavItems.map((item) => (
                      <DropdownMenuItem key={item.href} onClick={() => navigate(item.href)}>
                        <item.icon className="mr-2 h-4 w-4" />
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/overview"
              className="flex items-center"
            >
              <img
                src="/etherpilot-logo.png"
                alt="EtherPilot"
                className="h-8 w-8 rounded-sm"
              />
            </Link>
          </div>

          <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={() => navigate('/networks/add')}
            >
              <Plus className="h-4 w-4" />
              <span className="sr-only">New network</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Bell className="h-4 w-4" />
              <span className="sr-only">Notifications</span>
            </Button>
            <ThemeDropdown
              triggerVariant="ghost"
              triggerClassName="h-8 w-8 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full border border-border bg-background p-0 text-foreground hover:bg-muted"
                >
                  <span className="flex h-full w-full items-center justify-center rounded-full bg-[#f0c7aa] text-xs font-semibold uppercase">
                    {accountInitial}
                  </span>
                  <span className="sr-only">{user?.email || 'Account'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="space-y-1">
                  <span className="block text-xs uppercase tracking-[0.16em] text-muted-foreground">Account</span>
                  <span className="block truncate text-sm font-medium text-foreground">{user?.email || 'Signed in'}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/docs')}>
                  <BookOpen className="mr-2 h-4 w-4" />
                  Docs
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <aside className="fixed left-0 top-14 z-40 hidden h-[calc(100vh-3.5rem)] w-[12rem] border-r border-border bg-muted md:flex md:flex-col">
        <nav aria-label="Primary" className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {primaryNavItems.map((item) => (
            <AppShellNavLink key={item.href} item={item} pathname={location.pathname} />
          ))}
          {isAdmin && (
            <>
              <div className="my-3 border-t border-border/50" />
              <span className="flex items-center gap-2 px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                <Shield className="h-3 w-3" />
                Admin
              </span>
              {adminNavItems.map((item) => (
                <AppShellNavLink key={item.href} item={item} pathname={location.pathname} compact />
              ))}
            </>
          )}
        </nav>
        <div className="border-t border-border/50 px-3 py-3">
          <Link
            to="/docs"
            className="flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium tracking-tight text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <BookOpen className="h-[18px] w-[18px]" />
            <span>Docs</span>
          </Link>
        </div>
      </aside>

      <main className="min-h-screen pt-14 md:pl-[12rem]">
        <div className="mx-auto min-h-[calc(100vh-3.5rem)] w-full max-w-[112rem] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
          {content}
        </div>
      </main>
    </div>
  )
}
